import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { type NavigateFunction } from "react-router-dom";
import { useActions } from "./useActions";
import { tenantActionCreators } from "../store/modules/tenantModule";
import { airlineActionCreators } from "../../pages/AirlineLanding/airlineModule";
import { getMainSubdomainUrl } from "../utils/helpers/subdomainHelpers/getMainSubdomainUrl";

export type TransportLanguageRefreshOptions = {
  language: string;
  navigate: NavigateFunction;
  /** Path after the language segment, e.g. `/transport/airport-taxi` or empty string for airline landing. */
  pathSuffix: string;
  /** Preserve router state across language URL replace (airline transport pages). */
  getNavigationState?: () => Record<string, unknown> | undefined;
  /**
   * Provider-specific localized data refetch.
   * Use for providers guarded by `lastInitialFetchKeyRef` (Airport Taxi, CAT, Limos4).
   * Omit for providers that already refetch via a `[tenantInfo]` effect (SBB, Swiss Travel Pass).
   */
  onProviderLanguageChange?: () => void;
  /** IATA for GET /v1/airports/{iata}/transports; falls back to airline Redux state when omitted. */
  iataCode?: string | null;
};

/**
 * Syncs the URL when the user changes language and re-fetches localized data.
 *
 * Guest Platform (hotel):
 *   URL sync → AppFooter reloads tenant (useParams) → provider via `onProviderLanguageChange`
 *   OR tenant `[tenantInfo]` effect when `onProviderLanguageChange` is omitted.
 *
 * Airline:
 *   URL sync → tenant + airports + airport-transports → optional `onProviderLanguageChange`
 */
export function useTransportLanguageRefresh({
  language,
  navigate,
  pathSuffix,
  getNavigationState,
  onProviderLanguageChange,
  iataCode: iataCodeOverride
}: TransportLanguageRefreshOptions): void {
  const prevLanguageRef = useRef(language);
  const platformType = useSelector((state: any) => state.global.platformType);
  const defaultDomain = useSelector((state: any) => state.global.defaultDomain);
  const arrivalAirport = useSelector((state: any) => state.airline.arrivalAirport);

  const getTenantInfo = useActions(tenantActionCreators?.getTenantInfoAction, []);
  const getAvailableAirports = useActions(airlineActionCreators?.getAvailableAirportsAction, []);
  const getAirportOffers = useActions(airlineActionCreators?.getAirportOffersAction, []);

  useEffect(() => {
    if (prevLanguageRef.current === language) {
      return;
    }
    prevLanguageRef.current = language;

    const navState = getNavigationState?.();
    navigate(`/${language}${pathSuffix}`, {
      replace: true,
      ...(navState ? { state: navState } : {})
    });

    if (platformType !== "airline") {
      // Guest Platform: tenant reload is handled by AppFooter when the URL :language param
      // updates (do not call getTenantInfo here — that duplicated AppFooter).
      onProviderLanguageChange?.();
      return;
    }

    if (defaultDomain) {
      const tenantUrl = getMainSubdomainUrl(window.location.hostname, defaultDomain);
      getTenantInfo(tenantUrl, true, navigate);
    }

    getAvailableAirports();

    const iataCode = iataCodeOverride ?? arrivalAirport?.iata_code ?? arrivalAirport?.value ?? null;
    if (iataCode) {
      getAirportOffers(iataCode);
    }

    onProviderLanguageChange?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: run only on language change
  }, [language]);
}
